import { defineStore } from "pinia";
import { ref } from "vue";

import {
  createJobFamily,
  createJobFamilyLevel,
  deleteJobFamilyLevel,
  getJobFamily,
  getJobFamilyLevels,
  getListOfJobFamilies,
  reorderJobFamilyLevels,
  suggestJobFamilyLevelsStream,
  undoJobFamilyLevelSuggestion,
  updateJobFamily,
  updateJobFamilyLevel,
} from "@/api/jobFamilyAPI";

export const useJobFamilyStore = defineStore("jobFamily", () => {
  // In-memory, session-scoped suggestion snapshots keyed by `${accountId}:${familyId}`.
  // Kept here (not sessionStorage/localStorage) so the "Undo Suggestions" banner survives
  // in-app route changes but is cleared when the tab closes or reloads (ER-11826).
  const suggestionSnapshots = ref({});

  function suggestionKey(accountId, familyId) {
    return `${accountId}:${familyId}`;
  }

  function getSuggestionSnapshot(accountId, familyId) {
    return suggestionSnapshots.value[suggestionKey(accountId, familyId)] || null;
  }

  function setSuggestionSnapshot(accountId, familyId, snapshotId) {
    const key = suggestionKey(accountId, familyId);
    const next = { ...suggestionSnapshots.value };
    if (snapshotId == null || snapshotId === "") {
      delete next[key];
    } else {
      next[key] = snapshotId;
    }
    suggestionSnapshots.value = next;
  }

  function clearSuggestionSnapshot(accountId, familyId) {
    const next = { ...suggestionSnapshots.value };
    delete next[suggestionKey(accountId, familyId)];
    suggestionSnapshots.value = next;
  }

  async function fetchList(accountId, filters = {}, pagination = {}) {
    const { data } = await getListOfJobFamilies(accountId, filters, pagination);
    return data;
  }

  async function fetchOne(accountId, familyId) {
    const { data } = await getJobFamily(accountId, familyId);
    return data;
  }

  async function create(accountId, payload) {
    const { data } = await createJobFamily(accountId, payload);
    return data;
  }

  async function update(accountId, familyId, payload) {
    const { data } = await updateJobFamily(accountId, familyId, payload);
    return data;
  }

  async function fetchLevels(accountId, familyId) {
    const { data } = await getJobFamilyLevels(accountId, familyId);
    return data;
  }

  async function createLevel(accountId, familyId, payload) {
    const { data } = await createJobFamilyLevel(accountId, familyId, payload);
    return data;
  }

  async function updateLevel(accountId, familyId, levelId, payload) {
    const { data } = await updateJobFamilyLevel(accountId, familyId, levelId, payload);
    return data;
  }

  async function deleteLevel(accountId, familyId, levelId, reassignTo) {
    const { data } = await deleteJobFamilyLevel(accountId, familyId, levelId, reassignTo);
    return data;
  }

  async function reorderLevels(accountId, familyId, orderedLevelIds) {
    const { data } = await reorderJobFamilyLevels(accountId, familyId, orderedLevelIds);
    return data;
  }

  async function suggestLevels(accountId, familyId, signal, snapshotId, callbacks = {}) {
    const { onPhase, onThinking, onStalled } = callbacks;
    const { data } = await suggestJobFamilyLevelsStream({
      accountId,
      familyId,
      signal,
      snapshotId,
      onPhase,
      onThinking,
      onStalled,
    });
    return data;
  }

  async function undoSuggestion(accountId, familyId, snapshotId) {
    const { data } = await undoJobFamilyLevelSuggestion(accountId, familyId, snapshotId);
    return data;
  }

  return {
    suggestionSnapshots,
    getSuggestionSnapshot,
    setSuggestionSnapshot,
    clearSuggestionSnapshot,
    fetchList,
    fetchOne,
    create,
    update,
    fetchLevels,
    createLevel,
    updateLevel,
    deleteLevel,
    reorderLevels,
    suggestLevels,
    undoSuggestion,
  };
});
